import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import api from '../../lib/api';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);
  
  const checkPaymentStatus = async () => {
    try {
      const res = await api.get(`/payments/stripe/status/${sessionId}`);
      setPaymentInfo(res.data);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  };
  
  if (status === 'loading') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="stat-card text-center py-12">
            <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Verifying Payment...</h2>
            <p className="text-slate-400">Please wait while we confirm your payment</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (status === 'error') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="stat-card text-center py-12">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Verification Failed</h2>
            <p className="text-slate-400 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Button onClick={() => navigate('/payments')} className="gradient-accent">
              Return to Payments
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="stat-card text-center py-12">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
          <p className="text-slate-400 mb-6">
            Thank you for your payment. Your transaction has been completed.
          </p>
          
          {paymentInfo && (
            <div className="bg-slate-800/50 rounded-xl p-6 mb-6 text-left max-w-md mx-auto">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-400">Amount Paid</span>
                <span className="text-2xl font-bold text-emerald-400">
                  ${(paymentInfo.amount_total / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-400">Status</span>
                <span className="text-emerald-400 font-medium capitalize">
                  {paymentInfo.payment_status}
                </span>
              </div>
              {paymentInfo.metadata?.package_name && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Package</span>
                  <span className="font-medium">{paymentInfo.metadata.package_name}</span>
                </div>
              )}
            </div>
          )}
          
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/dashboard')} className="gradient-accent">
              Go to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/payments')}
              variant="outline"
              className="border-slate-700"
            >
              View Payment History
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
